import React, { useState } from 'react';

/**
 * TutorMatchCard
 *
 * Displays a single tutor match result with confidence score,
 * explanation, category breakdown, and an Assign button.
 * Adapted from MatchResult.jsx for use in the side panel.
 */
const TutorMatchCard = ({
  match,
  rank,
  onAssign,
  isAssigning = false,
  isAssigningThisTutor = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Determine confidence level color class
  const getConfidenceClass = (score) => {
    if (score >= 90) return 'tutor-match-card--high';
    if (score >= 75) return 'tutor-match-card--medium';
    return 'tutor-match-card--low';
  };

  const confidenceClass = getConfidenceClass(match.confidence_score);

  // Get confidence color for ring
  const getConfidenceColor = (score) => {
    if (score >= 90) return 'var(--lrbrain-success)';
    if (score >= 75) return 'var(--lrbrain-warning)';
    return 'var(--lrbrain-error)';
  };

  return (
    <div className={`tutor-match-card ${confidenceClass}`}>
      {/* Rank Badge */}
      <div className="tutor-match-card__rank">#{rank}</div>

      {/* Header with Avatar and Score */}
      <div className="tutor-match-card__header">
        <div className="tutor-match-card__avatar-container">
          {match.tutor_headshot ? (
            <img
              src={match.tutor_headshot}
              alt={match.tutor_name}
              className="tutor-match-card__avatar"
            />
          ) : (
            <div className="tutor-match-card__avatar tutor-match-card__avatar--placeholder">
              {match.tutor_name?.charAt(0)?.toUpperCase() || 'T'}
            </div>
          )}
          <div
            className="tutor-match-card__confidence-ring"
            style={{ borderColor: getConfidenceColor(match.confidence_score) }}
          />
        </div>

        <div className="tutor-match-card__info">
          <h4 className="tutor-match-card__name">{match.tutor_name}</h4>
          <div className="tutor-match-card__score">
            <div className="tutor-match-card__score-bar">
              <div
                className="tutor-match-card__score-fill"
                style={{ width: `${match.confidence_score}%` }}
              />
            </div>
            <span className="tutor-match-card__score-text">
              {match.confidence_score}% Match
            </span>
          </div>
        </div>
      </div>

      {/* Bio Preview */}
      {match.tutor_bio && (
        <p className="tutor-match-card__bio">
          {match.tutor_bio.substring(0, 120)}
          {match.tutor_bio.length > 120 ? '...' : ''}
        </p>
      )}

      {/* Expand/Collapse Details */}
      <button
        className="tutor-match-card__expand"
        onClick={() => setIsExpanded(!isExpanded)}
        type="button"
      >
        {isExpanded ? (
          <>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M3 7.5L6 4.5L9 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Hide Details
          </>
        ) : (
          <>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Why this match?
          </>
        )}
      </button>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="tutor-match-card__details">
          {/* Explanation */}
          {match.explanation && (
            <div className="tutor-match-card__explanation">
              <h5>Match Rationale</h5>
              <p>{match.explanation}</p>
            </div>
          )}

          {/* Category Breakdown */}
          {match.category_scores && (
            <div className="tutor-match-card__categories">
              <h5>Category Breakdown</h5>
              <div className="tutor-match-card__category-grid">
                <CategoryItem
                  label="Subject Expertise"
                  score={match.category_scores.subject_expertise || 0}
                />
                <CategoryItem
                  label="Learning Style"
                  score={match.category_scores.learning_style || 0}
                />
                <CategoryItem
                  label="Session Preferences"
                  score={match.category_scores.session_preferences || 0}
                />
                <CategoryItem
                  label="Personality Fit"
                  score={match.category_scores.personality_fit || 0}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Assign Button */}
      <button
        className="tutor-match-card__assign-btn"
        onClick={() => onAssign(match.tutor_id)}
        disabled={isAssigning}
        type="button"
      >
        {isAssigningThisTutor ? (
          <>
            <span className="tutor-match-card__assign-spinner" />
            Assigning...
          </>
        ) : (
          <>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M7 1V13M1 7H13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            Assign Tutor
          </>
        )}
      </button>
    </div>
  );
};

/**
 * CategoryItem - Helper component for category breakdown
 */
const CategoryItem = ({ label, score }) => {
  const getScoreColor = (s) => {
    if (s >= 90) return 'var(--lrbrain-success)';
    if (s >= 75) return 'var(--lrbrain-warning)';
    if (s >= 50) return 'var(--lrbrain-primary)';
    return 'var(--lrbrain-error)';
  };

  return (
    <div className="tutor-match-card__category-item">
      <div className="tutor-match-card__category-header">
        <span className="tutor-match-card__category-label">{label}</span>
        <span className="tutor-match-card__category-score">{score}%</span>
      </div>
      <div className="tutor-match-card__category-bar">
        <div
          className="tutor-match-card__category-fill"
          style={{
            width: `${score}%`,
            backgroundColor: getScoreColor(score)
          }}
        />
      </div>
    </div>
  );
};

export default TutorMatchCard;
